/**
 * insightsCalculations.js
 * All Insights data crunching happens here on the client side.
 * Backend only returns raw receipt rows; this file aggregates them.
 */

export const MINIMUM_RECEIPTS = 6;

// Sentinel month-key meaning "the whole 3-month window" rather than one month.
export const ALL_KEY = 'ALL';

// ─── Month helpers ────────────────────────────────────────────────────────────

/** Return { year, month } for a Date object (month is 0-based). */
const ym = (d) => ({ year: d.getFullYear(), month: d.getMonth() });

/** Build the last N month descriptors (newest first). Default 3-month window. */
export const buildMonthRange = (n = 3) => {
  const now = new Date();
  const months = [];
  for (let i = 0; i < n; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({
      year: d.getFullYear(),
      month: d.getMonth(),             // 0-based
      key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-GB', { month: 'short' }),      // 'May'
      labelLong: d.toLocaleDateString('en-GB', { month: 'long' }),   // 'May'
      labelFull: d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }), // 'May 2026'
    });
  }
  return months; // index 0 = current month
};

// ─── Receipt parsing helpers ──────────────────────────────────────────────────

/** Parse "YYYY-MM-DD" as local midnight (not UTC). Full ISO timestamps pass through normally. */
const parseLocalDate = (raw) => {
  if (!raw) return null;
  // Date-only strings (e.g. "2026-05-31") are parsed as UTC by JS — split and construct locally
  if (typeof raw === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const [y, m, d] = raw.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : d;
};

/** Parse a receipt's date from transaction_date or created_at. */
const receiptDate = (r) => {
  const raw = r.transaction_date || r.created_at;
  return parseLocalDate(raw);
};

// ─── Core aggregation ─────────────────────────────────────────────────────────

/**
 * Given all user receipts (raw from API), compute everything Insights needs.
 *
 * NOTE: The caller (InsightsPage) is responsible for currency filtering.
 * This function works with whatever receipts are passed in — no internal
 * currency filter is applied.
 *
 * @param {Array}  allReceipts  - All user receipts (already status:active from API)
 * @param {{ year, month }}  selectedMonth  - Which month to show details for
 * @returns InsightData object
 */
export const computeInsights = (allReceipts = [], selectedMonth, options = {}) => {
  // allHistory: true → aggregate over EVERY receipt, no 3-month window cap.
  // Used for finite group types (trip/event/custom) so year-old receipts are never dropped.
  const allHistory = options.allHistory === true;

  // 1. Total receipt count (all time) for threshold logic
  const totalReceiptCount = allReceipts.length;
  const hasEnoughData = totalReceiptCount >= MINIMUM_RECEIPTS;

  // 2. Build 3-month window (for month chips and per-month comparison bars)
  const monthRange = buildMonthRange(3);

  // 3. Default selected month = current month. ALL = whole window (or all history).
  const sel = selectedMonth || monthRange[0];
  const isAll = allHistory || sel?.key === ALL_KEY;

  // 4. Bucket receipts by month-key for the 3-month window
  const monthKeys = new Set(monthRange.map((m) => m.key));
  const buckets = {};
  monthRange.forEach((m) => { buckets[m.key] = []; });

  allReceipts.forEach((r) => {
    const d = receiptDate(r);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (monthKeys.has(key)) buckets[key].push(r);
  });

  // 5. Per-month totals (for comparison)
  const monthTotals = {};
  monthRange.forEach((m) => {
    const total = buckets[m.key].reduce((s, r) => s + Number(r.amount || 0), 0);
    monthTotals[m.key] = { total, count: buckets[m.key].length };
  });

  // 6. Selected period receipts:
  //    allHistory → every receipt regardless of age (no 3-month window)
  //    ALL        → concat all 3-month buckets
  //    month      → that month's bucket only
  const selKey = isAll ? ALL_KEY : sel.key;
  const selReceipts = allHistory
    ? allReceipts.slice()
    : isAll
      ? monthRange.reduce((acc, m) => acc.concat(buckets[m.key]), [])
      : (buckets[selKey] || []);
  const selTotal = selReceipts.reduce((s, r) => s + Number(r.amount || 0), 0);
  const selCount = selReceipts.length;

  // 7. Previous month comparison — not meaningful in ALL / allHistory mode
  const selMonthIdx = isAll ? -1 : monthRange.findIndex((m) => m.key === selKey);
  const prevMonth = (!isAll && selMonthIdx >= 0) ? (monthRange[selMonthIdx + 1] || null) : null;
  const prevKey = prevMonth?.key;
  const prevTotal = prevKey ? (monthTotals[prevKey]?.total || 0) : 0;

  let vsLastMonthPct = null;
  let vsLastMonthDir = null; // 'up' | 'down' | 'same'
  if (prevMonth && prevTotal > 0) {
    const diff = selTotal - prevTotal;
    vsLastMonthPct = Math.round(Math.abs(diff / prevTotal) * 100);
    vsLastMonthDir = diff > 0 ? 'up' : diff < 0 ? 'down' : 'same';
  } else if (prevMonth && prevTotal === 0 && selTotal > 0) {
    vsLastMonthPct = 100;
    vsLastMonthDir = 'up';
  }

  // 8. Category breakdown for selected month
  const categoryMap = {};
  selReceipts.forEach((r) => {
    const cat = r.category || 'Other';
    if (!categoryMap[cat]) categoryMap[cat] = { total: 0, count: 0 };
    categoryMap[cat].total += Number(r.amount || 0);
    categoryMap[cat].count += 1;
  });
  const categories = Object.entries(categoryMap)
    .map(([name, { total, count }]) => ({
      name,
      total,
      count,
      pct: selTotal > 0 ? Math.round((total / selTotal) * 100) : 0,
    }))
    .sort((a, b) => b.total - a.total);

  // 9. Top Merchants for selected month (max 4, sorted by total amount DESC)
  const merchantMap = {};
  selReceipts.forEach((r) => {
    const m = r.merchant || 'Unknown';
    if (!merchantMap[m]) merchantMap[m] = { name: m, total: 0, count: 0 };
    merchantMap[m].total += Number(r.amount || 0);
    merchantMap[m].count += 1;
  });
  const topMerchants = Object.values(merchantMap)
    .sort((a, b) => b.total - a.total || b.count - a.count)
    .slice(0, 4);

  // 10. Top Purchases of the month (max 4, sorted by individual item price DESC)
  // Flatten every line item from every receipt in the selected month,
  // then sort by item price so we surface the single most costly products —
  // not the most expensive receipts (which would duplicate topMerchants).
  const allItems = [];
  selReceipts.forEach((r) => {
    if (r.items && r.items.length > 0) {
      r.items.forEach((item) => {
        // Use the actual receipt item field names from the scan pipeline
        const itemPrice = Number(item.final_total || item.original_total || item.unit_price || 0);
        if (itemPrice > 0) {
          allItems.push({
            id: r.id,
            merchant: r.merchant || 'Unknown',
            category: r.category || null,
            transaction_date: r.transaction_date,
            created_at: r.created_at,
            topItemName: item.name || item.description || 'Item',
            amount: itemPrice,
          });
        }
      });
    } else {
      // Receipt has no itemised data — fall back to the receipt total
      allItems.push({
        id: r.id,
        merchant: r.merchant || 'Unknown',
        category: r.category || null,
        transaction_date: r.transaction_date,
        created_at: r.created_at,
        topItemName: 'General Purchase',
        amount: Number(r.amount || 0),
      });
    }
  });

  const topPurchases = allItems
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 4);

  // 11. Monthly comparison data (for the comparison section)
  const monthComparison = monthRange.slice(0, 3).map((m) => ({
    ...m,
    total: monthTotals[m.key]?.total || 0,
    count: monthTotals[m.key]?.count || 0,
  })).reverse(); // oldest → newest for display

  return {
    hasEnoughData,
    totalReceiptCount,
    monthRange,
    selectedMonth: sel,
    selKey,
    selReceipts,
    selTotal,
    selCount,
    prevMonth,
    prevTotal,
    vsLastMonthPct,
    vsLastMonthDir,
    categories,
    topMerchants,
    topPurchases,
    monthComparison,
  };
};

// ─── Group: spend by member ───────────────────────────────────────────────────

/**
 * Group a set of receipts by the member who shared them.
 *
 * @param {Array} receipts  - receipts already scoped to the selected period
 *                            (each must carry `shared_by` + `shared_by_name`)
 * @param {Array} members   - group members [{ user_id, display_name }] so members
 *                            with zero spend in this period still appear
 * @returns Array sorted by total DESC. Each member:
 *   {
 *     user_id, name, total, count,
 *     receipts: [{ id, merchant, date, amount }],          // flat, newest-first
 *     months:   [{ key, label, total, receipts:[...] }],   // grouped, newest-first
 *   }
 */
export const computeMemberSpend = (receipts = [], members = []) => {
  const map = {};

  const seed = (uid, name) => {
    if (!map[uid]) {
      map[uid] = { user_id: uid, name: name || 'Member', total: 0, count: 0, receipts: [] };
    }
  };

  // Seed every active member so zero-spend members still show
  members.forEach((m) => seed(m.user_id, m.display_name));

  receipts.forEach((r) => {
    const uid = r.shared_by || 'unknown';
    seed(uid, r.shared_by_name);
    const amt = Number(r.amount || 0);
    map[uid].total += amt;
    map[uid].count += 1;
    map[uid].receipts.push({
      id: r.id,
      merchant: r.merchant || 'Unknown',
      date: r.transaction_date || r.created_at,
      amount: amt,
    });
  });

  const list = Object.values(map);

  list.forEach((m) => {
    // Flat list — newest first
    m.receipts.sort((a, b) => {
      const da = parseLocalDate(a.date);
      const db_ = parseLocalDate(b.date);
      return (db_?.getTime() || 0) - (da?.getTime() || 0);
    });

    // Month-grouped breakdown — months newest-first, receipts newest-first within.
    // Caller can show month headers only when months.length > 1.
    const monthMap = {};
    m.receipts.forEach((r) => {
      const d = parseLocalDate(r.date);
      if (!d) return;
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthMap[key]) {
        monthMap[key] = {
          key,
          label: d.toLocaleDateString('en-GB', { month: 'long', year: 'numeric' }),
          total: 0,
          receipts: [],
        };
      }
      monthMap[key].total += r.amount;
      monthMap[key].receipts.push(r); // inherits newest-first order from flat sort
    });
    m.months = Object.values(monthMap).sort((a, b) => (a.key < b.key ? 1 : -1));
  });

  // Members sorted by spend DESC; zero-spend members fall to the bottom
  return list.sort((a, b) => b.total - a.total || b.count - a.count);
};

// ─── Shared window helper ─────────────────────────────────────────────────────

/**
 * First day of the rolling N-month window as 'YYYY-MM-DD'.
 * 25 Jun 2026 → '2026-04-01'; 10 Jul 2026 → '2026-05-01'.
 */
export const insightsWindowStart = (months = 3) => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - (months - 1), 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};